import { lighten, styled, Menu, ButtonProps } from "@mui/material"

import { useState } from "react"
import { CisQueryType } from "@tm/models"
import { Icon } from "../../muiStyled/DataDisplay/Icons"
import { MenuItem } from "../../muiStyled/Inputs/select"
import { Button } from "../../muiStyled/Inputs/button"

const SearchFieldSelectButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== "active",
})<{ active?: boolean }>(({ theme, active, variant }) => {
    const { button } = theme.overwrites?.components ?? {}
    const { getContrastText } = theme.palette
    const primaryButton = button?.primary

    if (variant === "outlined") {
        return {
            borderLeft: "none",
            borderTop: "none",
            borderBottom: "none",
            borderRadius: 0,
            "&:hover": {
                borderLeft: "none",
                borderTop: "none",
                borderBottom: "none",
            },
        }
    }

    if (!variant) {
        const defaultBackgroundColor = primaryButton?.backgroundColor ?? theme.palette.grey[300]
        const backgroundColorHover = primaryButton?.hover?.backgroundColor ?? lighten(theme.palette.primary.main, 0.5)
        const backgroundContrastColor = getContrastText(defaultBackgroundColor)
        const focusedColor = primaryButton?.focus ?? theme.palette.primary.light
        const focusContrastColor = getContrastText(focusedColor)
        const activeStyles = {
            boxShadow: "unset !important",
            border: `1px solid ${focusedColor} !important`,
            backgroundColor: `${focusedColor}!important`,
            color: focusContrastColor,
            svg: {
                fill: focusContrastColor,
            },
        }

        return {
            color: backgroundContrastColor,
            border: `1px solid ${defaultBackgroundColor} !important`,
            borderRadius: `${theme.radius?.default || "2px"} !important`,
            backgroundColor: `${defaultBackgroundColor}!important`,
            borderTopRightRadius: "0 !important",
            borderBottomRightRadius: "0 !important",
            pointerEvents: "auto",
            fontSize: "16px !important", // force fontSize to make sure this select has the same fontSize as our select
            svg: {
                fill: backgroundContrastColor,
            },
            ":hover": {
                backgroundColor: `${backgroundColorHover} !important`,
                color: backgroundContrastColor,
                "&.Mui-disabled": {
                    backgroundColor: defaultBackgroundColor,
                },
            },
            ".Mui-focused &": activeStyles,
            ...(active && activeStyles),
        }
    }
    return {}
})

export type SearchFieldSelectEntryType = { id: number; description: string; [key: string]: unknown }

type SearchFieldSelectProps = {
    handleValueChange: (entry: SearchFieldSelectEntryType) => void
    entries?: SearchFieldSelectEntryType[]
    value: SearchFieldSelectEntryType
    buttonVariant?: ButtonProps["variant"]
}

/**
 * <Select> is not used inside the TextField because it would render an <input>,
 * and only one input is allowed inside a FormControl (MUI constraint).
 * See: https://jira.dvse.de/browse/NEXT-31058
 */
export function SearchFieldSelect(props: SearchFieldSelectProps) {
    const { handleValueChange, entries, value, buttonVariant } = props
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
    const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget)
    }

    const handleCloseMenu = () => {
        setAnchorEl(null)
    }

    const handleSelect = (option: CisQueryType) => {
        handleValueChange(option)
        handleCloseMenu()
    }

    return (
        <>
            <SearchFieldSelectButton
                variant={buttonVariant}
                onClick={handleOpenMenu}
                endIcon={<Icon name={anchorEl ? "up" : "down"} />}
                active={!!anchorEl}
            >
                {value.description}
            </SearchFieldSelectButton>
            <Menu
                anchorEl={anchorEl}
                open={!!anchorEl}
                onClose={handleCloseMenu}
                PaperProps={{ sx: { minWidth: `${anchorEl?.clientWidth}px` } }}
                MenuListProps={{
                    sx: (theme) => ({
                        backgroundColor: theme.overwrites?.components?.select?.menu?.backgroundColor || "#fff",
                        border: 1,
                        borderColor: theme.overwrites?.components?.select?.menu?.borderColor || theme.palette.primary.main,
                    }),
                }}
            >
                {entries?.map((item) => (
                    <MenuItem key={item.description} value={item} onClick={() => handleSelect(item)} selected={item.id === value.id}>
                        {item.description}
                    </MenuItem>
                ))}
            </Menu>
        </>
    )
}
